import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import { ToastProvider } from './components/ToastContext';
import { BrandingProvider } from './components/BrandingContext';
import { PageBarProvider } from './components/PageBar';
import { OrgProvider } from './components/OrgContext';
import { AnnouncementsProvider } from './components/Announcements';
import Boundary from './components/Boundary';
import { NotFound } from './pages/SystemStates';
import SetNewPassword from './pages/SetNewPassword';
import { trace } from './lib/trace';
import { OnHandLogo } from './components/brand/OnHandLogo';
import { start as startOutbox, onRefused } from './lib/outbox';
import { startWatching } from './lib/sheetWatch';
import { useToast } from './components/ToastContext';
import LoginPage       from './pages/LoginPage';
import SignedInButStuck from './pages/SignedInButStuck';
import RepDashboard    from './pages/RepDashboard';
import CountEntry      from './pages/CountEntry';
import AdminDashboard  from './pages/AdminDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import BuyerDashboard   from './pages/BuyerDashboard';
import './index.css';
/* THE PHASE 3 DESIGN SYSTEM, job 29, live since 13 September 2026. The new look
   laid over the app (its moving background retired by theme phase 3, 14
   September 2026). Loaded after index.css
   so its token values win. See src/phase3/settings.js and docs/design/phase-3/. */
import './phase3/phase3.css';
/* THE THEME SYSTEM, from 14 September 2026. docs/THEME-SYSTEM.md. The brand
   colour foundation and the role tokens: definitions only, drawing nothing on
   their own until screens ask for a role. */
import './theme/foundation.css';
import './theme/roles.css';
/* The fourteen themes themselves are NOT imported here: public/theme/themes.css,
   linked from public/index.html. See the note there. */
/* THE ONHAND BRAND, from 16 September 2026, the design record: docs/brand/.
   Rebrand step 1. Figtree, the brand's one typeface, served with the site from
   the @fontsource/figtree package rather than fetched from Google, in the four
   weights the guide uses (section 5.1). Each file covers every script with a
   unicode-range, so a browser only downloads the Latin half it needs. */
import '@fontsource/figtree/400.css';
import '@fontsource/figtree/600.css';
import '@fontsource/figtree/700.css';
import '@fontsource/figtree/800.css';
/* The brand's tokens, an exact copy of docs/brand/tokens/onhand-tokens.css (a
   test fails if the two ever differ). Loaded LAST, so where it and an older file
   define the same name at the same strength, the brand's value wins. Step 1
   retired the older definitions of the names they shared, so there is one value
   for each. It needs data-color-mode on <html>, which src/theme/appearance.js
   sets beside data-mode. Its theme blocks (data-theme open, shelves...) wait for
   rebrand step 6; until then data-theme still holds the fourteen current
   themes, the owner's decision of 16 September 2026. */
import './styles/brand-tokens.css';
/* The brand's components (rebrand step 3): shape, size, depth, glass and motion
   for buttons, fields, cards, tables, badges, menus, dialogs, messages, empty
   states and skeletons. Colours stay the theme's until step 6. */
import './styles/brand-components.css';
import './styles/brand-layout.css';
import './styles/brand-counting.css';
/* The ten themes' colours, mapped onto this app's roles (rebrand step 6). It
   loads after public/theme/themes.css, which index.html links. */
import './theme/brandThemes.css';
import { AppShell } from './components/AppFrame';
import { apply as applyPhase3 } from './phase3/settings';

/*
  Opening OnHand: the first thing anybody ever sees.

  **Rebuilt 10 September 2026 for job 25**, to Group H's approved drawing. It
  was a grey ring and the word "Loading..." on a bare page.

  **Two changes, both small and both the group's own.** The wordmark goes above
  the spinner, so the first thing on screen is the name of the thing you opened
  rather than an anonymous ring that could belong to any website. And the word
  goes: a spinner already says "wait", and writing it underneath is the app
  narrating itself, which is the same habit as the Coming Soon page explaining
  its own menu to the person reading it.

  **No shell**, rule 35, and this is the clearest case of the three: nobody is
  known yet, so a bar carrying somebody's name and company cannot be drawn.

  **Three sizes, and the smallest was found by measuring rather than by
  looking.** 104 looks perfectly reasonable in a picture at 390 and is 384
  pixels wide against a row that has 366, so the wordmark ran off both edges of
  the phone. The eye reported nothing.

  **It is the STACKED lockup since 11 September 2026**, job 27 phase 1. The brand
  guide's section 4.1 names the stacked lockup for exactly this, an app splash,
  and the change also retires the width problem above rather than repeating it:
  stacked, the widest thing on the row is the word rather than the word plus the
  mark plus a gap. The three sizes are the mark's height and the word follows at
  0.55 of it, which is the guide's own proportion.
*/
function Waiting() {
  const [size, setSize] = React.useState(() => bootLogoSize());
  React.useEffect(() => {
    const sync = () => setSize(bootLogoSize());
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);
  return (
    <div className="boot-screen">
      <OnHandLogo layout="stacked" iconSize={size} animate="launch" />
      {/* A quiet loading bar in place of the spinner, rebrand step 3 (guide
          10.5: skeletons everywhere, and the logo is never a full-page loader). */}
      <div className="skel-block boot-bar" role="status" aria-label="Loading" />
    </div>
  );
}

function bootLogoSize() {
  const w = typeof window === 'undefined' ? 1440 : window.innerWidth;
  return w >= 1024 ? 96 : w >= 700 ? 80 : 64;
}

/*
  Exported since 10 September 2026 so a test can render it. **The thing worth
  proving is that a bad address is ANSWERED rather than swallowed**, and a
  silent redirect to the home page looks exactly like a working app from
  outside, which is how it survived unnoticed until job 25.
*/
export function AppRoutes() {
  const { user, loading, recovering } = useAuth();
  /*
    **The sign-in page stays on screen until its sequence has played**, 18
    September 2026, after the owner saw a successful sign-in skip straight to
    a white loading screen. Supabase announces the new session from inside the
    sign-in request, and this function used to answer that at once by taking
    the sign-in page away, so the box never closed on the card, the "Signed
    in!" label never landed and the sweep never ran. Nothing was broken except
    the whole of what the guide's 9.6 describes, and the step 7 tests stood the
    sign-in system in, so none of them could see it.

    So the sign-in page reports where it is (`onSequence`):
      idle      nothing is playing: the usual rule decides
      playing   a sign-in is under way: the sign-in page, and only it
      leaving   the sweep: the app is drawn underneath and rides in from the
                right as the sign-in page rides off to the left

    **The two always sit at the same place in this list**, the app first and
    the sign-in page second, so React keeps the sign-in page's own state (the
    label, the sweep) when the app appears beside it. `AppScreens` never draws
    the sign-in page itself.
  */
  const [signin, setSignin] = React.useState('idle');
  const wantsSignIn = !loading && !recovering && !user;
  const showSignIn = wantsSignIn || signin !== 'idle';
  const showApp = !wantsSignIn && signin !== 'playing';
  /*
    The warehouse picture public/index.html puts up before the app loads is
    for the sign-in page, which takes it down itself. If the answer turns out to
    be "already signed in" (another OnHand tab is open), take it down here.
  */
  React.useEffect(() => {
    if (!loading && user) document.documentElement.classList.remove('oh-signin-boot');
  }, [loading, user]);
  if (wantsSignIn) trace('screen', { showing: 'sign-in' });
  else if (signin !== 'idle') trace('screen', { showing: 'sign-in-sequence', phase: signin });
  return (
    <>
      {showApp ? <AppScreens /> : null}
      {showSignIn ? <LoginPage onSequence={setSignin} /> : null}
    </>
  );
}

function AppScreens() {
  const { user, profile, loading, profileLoading, recovering } = useAuth();

  if (loading) return <Waiting />;

  /*
    **Somebody who followed a reset link sees one screen and nothing else.**
    Added 10 September 2026 with self-service password reset.

    Following the link signs them in for real, so without this they would land
    on their home page with their old password still in force and no sign that
    the link had done anything at all. It is deliberately ABOVE every other
    branch below, including the one for a signed-in person with no record: the
    only thing this session is for is choosing a password.
  */
  if (recovering) { trace('screen', { showing: 'set-new-password' }); return <SetNewPassword email={user?.email} />; }

  /*
    **A sign-in screen with a session behind it is a lie, and it is what "I sign
    in and nothing happens" actually looked like.** Added 7 September 2026, after
    the owner reported the same thing five or six times.

    Signing in was working every time. What failed afterwards was reading his own
    record, and the app answered that by showing the sign-in screen again, with
    nothing said. So a person types the right password, the screen comes back
    blank, and the only available conclusion is that the password was wrong.

    **If there is a session and no record, say so.** It is the difference between
    a fault somebody can report and a fault that looks like their own mistake.
  */
  /*
    **Still asking is not the same as asked and got nothing.** Added 8 September
    2026, after the owner filmed signing in and caught this screen flashing up
    for about half a second on the way to his home page.

    There is a gap of a second or so between Supabase saying he is signed in and
    OnHand having his record: the read is deliberately made a moment later,
    because Supabase must not be asked anything from inside its own callback,
    and it asks again twice if the first answer is empty, which it usually is.
    This branch could not tell that gap apart from a read that had finished and
    found nothing, so it put a screen headed "OnHand could not load your
    account" in front of a sign-in that was working perfectly.

    **Nothing was broken, and it looked exactly like something was**, which for a
    screen whose whole job is to tell somebody the truth about a fault is the
    worst thing it could do.
  */
  // What the screen decided to show, which is the thing the owner actually sees
  // and the one fact nothing has ever recorded.
  if (user && !profile && profileLoading) { trace('screen', { showing: 'waiting-for-record', user: user.id }); return <Waiting />; }
  if (user && !profile) { trace('screen', { showing: 'stuck', user: user.id }); return <SignedInButStuck user={user} />; }
  if (!user) return null;   // the sign-in page: AppRoutes draws it, see above
  trace('screen', { showing: 'app', role: profile.role });

  /*
    **The job level here is the one held AT THIS SITE**, not the one stored on
    the person: `AuthContext` swaps it in when the profile is loaded, because
    somebody can be an administrator at one company and a rep at another. Since 5
    September 2026 every rule in the database reads the same one, so this is a
    convenience rather than a gate, which is what a menu should be.

    **A buyer used to land here and get the rep's screens**, because there was no
    branch for one: somebody whose job is ordering stock was shown screens asking
    them to count it. Nobody holds that job today, so it was latent rather than
    live, and silent.
  */
  const isAdmin   = profile.role === 'admin';
  const isManager = profile.role === 'manager';
  const isBuyer   = profile.role === 'buyer';

  return (
    <Routes>
      {/*
        **Every signed-in page sits in the one shared frame**, which draws the
        theme's still background behind all of them. Theme system phase 3, 14
        September 2026; see src/components/AppFrame.jsx. The screens above this
        return (waiting, stuck, sign-in, setting a password) are outside it on
        purpose: nobody is known there, so there is no theme.
      */}
      <Route element={<AppShell />}>
      <Route path="/login" element={<Navigate to="/" />} />
      <Route path="/"
        element={isAdmin ? <AdminDashboard />
               : isManager ? <ManagerDashboard />
               : isBuyer ? <BuyerDashboard />
               : <RepDashboard />}
      />
      <Route path="/count/:countId" element={<CountEntry />} />
      {/*
        **An unknown address used to go silently to the home page.** Changed 10
        September 2026 for job 25, from Group H's approved drawing.

        A person following a mistyped or stale link was put on Home with nothing
        said, so the app looked like it had ignored them: they pressed a link,
        the screen changed to something they did not ask for, and there was no
        way to tell whether the link was wrong or OnHand was.

        Rule 35: the app is fine and only this address is not, so the screen
        keeps the menu. Taking it away would turn a wrong turn into a dead end.
      */}
      <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/*
  **The numbers a rep typed outlive the screen that made them**, so the queue is
  started here rather than inside count entry. Added 10 September 2026 with job
  25's part two.

  A rep whose battery died in a basement opens OnHand the next morning with
  numbers still on the phone. If nothing read the queue until they happened to
  reopen that one count sheet, those numbers would sit there unsent while the
  cycle closed around them.
*/
function useOutbox() {
  React.useEffect(() => startOutbox(), []);
}

/*
  **A held number that is refused has to be said out loud, and nothing was
  listening.** Found on 10 September 2026 while checking the queue rather than
  by anything failing.

  A number held on the phone is sent later, and later it can be refused rather
  than delayed: the cycle closed while the rep was in the basement, the sheet was
  handed to somebody else, the line was removed. The queue correctly drops a
  refusal rather than retrying it for ever, and until this existed it dropped it
  in silence. **The rep would have seen the bar clear and read that as their
  work landing.** That is the exact fault the whole of this job is about, built
  into the cure for it.

  It sits here rather than in count entry, because the rep may well have moved
  on by the time the signal comes back, and the whole point of the queue is that
  the numbers outlive the screen that made them.
*/
/*
  **"Tell me when it is free", the telling half.** Built 10 September 2026 with
  job 25, and it lives here for the same reason the outbox does: the rep asked
  from the count sheet, and by the time the sheet frees they have almost
  certainly moved on to another screen. A message that only appears on the
  screen you were standing on when you asked is not a message.
*/
function SheetWatchReports() {
  const toast = useToast();
  const navigate = useNavigate();
  React.useEffect(() => startWatching((w) => {
    toast.success(`${w.name || 'A count sheet'} is free now | Nobody has it open. Open it before somebody else does.`);
  }), [toast, navigate]);
  return null;
}

function OutboxReports() {
  const toast = useToast();
  React.useEffect(() => onRefused((job) => {
    toast.error(`A count of ${job.qty === null ? 'blank' : job.qty} for item ${job.itemNumber} could not be saved and has not been kept. `
      + 'It was typed while there was no signal. Count that line again.');
  }), [toast]);
  return null;
}

applyPhase3();

export default function App() {
  useOutbox();
  /*
    **Everything is inside a boundary now.** Without one, a component that throws
    while drawing takes the whole app down and the person is left with nothing
    and no explanation. Rule 35 says a crash gets a screen; until 7 September
    2026 it got a blank page.
  */
  return (
    <Boundary>
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <OrgProvider>
            <BrandingProvider>
              <PageBarProvider>
                {/*
                  **Round the routes rather than beside them, since 10 September
                  2026.** The terms still cover everything a person can reach, so
                  a rep signing in to count meets the same gate as an admin
                  opening a cycle. What changed is the release note: it is no
                  longer a box over a dimmed page, it is a notice at the top of
                  Home, so Home has to be able to reach it. One call still
                  answers both.
                */}
                <AnnouncementsProvider>
                  <AppRoutes />
                </AnnouncementsProvider>
                {/* Says so when a number held on the phone turns out to be
                    refused rather than merely late. Inside the toast provider,
                    because that is the only thing it needs. */}
                <OutboxReports />
                <SheetWatchReports />
              </PageBarProvider>
            </BrandingProvider>
          </OrgProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
    </Boundary>
  );
}
